import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { selectCurrentUser } from '../auth/authSlice';
import { useGetUserEvaluationSessionsQuery, useGetEvaluationStatsQuery, useGetCoachingTipsQuery, useGetUserDynamicPersonasQuery } from './userDashboardApiSlice';
import { useGetUserQuery } from '../users/usersApiSlice';
import { useGetQuizStatsQuery, useGetPKEStatsQuery } from '../product_knowledge/productKnowledgeApiSlice';
import { useCreateChatSessionMutation } from '../chat_sessions/chatSessionApiSlice';
import { Container, Header, Loader, Message, Grid, Dropdown, List, Progress, Statistic, Icon, Card, Label, Button, Segment } from 'semantic-ui-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler,
} from 'chart.js';
import { Line, Radar, Bar, Doughnut } from 'react-chartjs-2';
import moment from 'moment';
import { tagLabel } from '../evaluation_sessions/evaluationRules';
import { PAGE_SIZE } from '../../constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
);

const UserDashboardPage = () => {
    const [selectedTag, setSelectedTag] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [startingPersonaId, setStartingPersonaId] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const userIdParam = queryParams.get("userId");

    const [createChatSession] = useCreateChatSessionMutation();

    const handleStartTraining = async (personaId) => {
        setStartingPersonaId(personaId);
        try {
            const result = await createChatSession({ persona: personaId, language: "ko" }).unwrap();
            navigate(`/audio-orb-openai?session_id=${result.id}`);
        } catch (err) {
            console.error("Failed to create chat session:", err);
        } finally {
            setStartingPersonaId(null);
        }
    };

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const currentUser = useSelector(selectCurrentUser);
    // 회사 평균, and everything measured against it, is for admins only (client request,
    // 2026-10-19). Trainees see their own average and how far they have come from their lowest
    // score. Same rule as the stats endpoint's permission (IsStaffOrContractorAdmin).
    const isAdmin = !!(currentUser?.is_staff || currentUser?.is_contractor_admin);
    
    // If userIdParam is present, fetch that user. Otherwise use currentUser.
    // We only need to fetch if userIdParam is different from currentUser.id, 
    // but for simplicity, if userIdParam exists, we can try to fetch it or just use the ID.
    // To get the username, we should fetch the user details if it's not the current user.

    const { data: fetchedUser } = useGetUserQuery(userIdParam, {
        skip: !userIdParam,
    });

    const targetUser = userIdParam ? fetchedUser : currentUser;
    const targetUserId = userIdParam || currentUser?.id;

    const { data: sessions, isLoading, isError, error } = useGetUserEvaluationSessionsQuery(targetUserId, {
        skip: !targetUserId,
    });

    const { data: stats, isLoading: isStatsLoading } = useGetEvaluationStatsQuery(undefined, { skip: !isAdmin });
    const { data: coachingTips } = useGetCoachingTipsQuery(targetUserId, { skip: !targetUserId });
    const { data: quizStats, isLoading: isQuizStatsLoading } = useGetQuizStatsQuery(targetUserId, { skip: !targetUserId });
    const { data: pkeStats, isLoading: isPKEStatsLoading } = useGetPKEStatsQuery(targetUserId, { skip: !targetUserId });
    const { data: dynamicPersonas } = useGetUserDynamicPersonasQuery(targetUserId, { skip: !targetUserId });

    if (isLoading) return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader active inline='centered' />
        </div>
    );
    if (isError) return null;

    // Process data for chart
    // Note: The API only returns total_score in the list view. 
    // Detailed breakdown per criterion is not available without fetching individual session details.
    // Sort by date
    const sessionList = sessions?.results || sessions || [];
    const sortedSessions = [...sessionList].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Process data for Radar Chart (Strengths/Weaknesses by Tag)
    // We aggregate data from all sessions to show overall performance per tag
    const tagStats = {};
    
    sortedSessions.forEach(session => {
        // Check if session has tag_results (populated by backend)
        // Note: The list serializer might not include 'result' field by default.
        // If it's missing, we might need to update the serializer or fetch details.
        // Assuming 'result' is available or we use what we have.
        // If 'result' is not in the list view, we can't do this without fetching details.
        // Let's assume for now we might need to fetch it or it's there.
        // Actually, EvaluationSessionBriefSerializer does NOT include 'result'.
        // We need to update the serializer to include 'result' or at least 'tag_results' if we want to avoid N+1 calls.
        
        // However, for now, let's check if 'result' exists and has 'tag_results'
        if (session.result && session.result.tag_results) {
            Object.entries(session.result.tag_results).forEach(([tag, criteriaList]) => {
                if (tag === 'deduction') return;
                if (!tagStats[tag]) {
                    tagStats[tag] = { total: 0, met: 0, score: 0, max_score: 0 };
                }
                
                criteriaList.forEach(c => {
                    tagStats[tag].total += 1;
                    if (c.is_criterion_met) tagStats[tag].met += 1;
                    // Assuming score is available in criterion_data
                    // If not, we can use is_criterion_met as a proxy (1 or 0)
                    // But let's use the percentage of met criteria for the radar chart
                });
            });
        }
    });

    const radarLabels = Object.keys(tagStats);
    const radarDataPoints = radarLabels.map(tag => {
        const stats = tagStats[tag];
        return stats.total > 0 ? (stats.met / stats.total) * 100 : 0;
    });

    const radarData = {
        labels: radarLabels.map(tagLabel),
        datasets: [
            {
                label: '영역별 성과 (기준 충족 %)',
                data: radarDataPoints,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
            ...(stats && stats.tag_averages ? [{
                label: '회사 평균',
                data: radarLabels.map(tag => stats.tag_averages[tag] || 0),
                backgroundColor: 'rgba(255, 159, 64, 0.2)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
            }] : [])
        ],
    };

    const radarOptions = {
        onClick: (event, elements) => {
            if (elements.length > 0) {
                const index = elements[0].index;
                const label = radarData.labels[index];
                setSelectedTag(label);
            }
        },
        scales: {
            r: {
                angleLines: {
                    display: false
                },
                suggestedMin: 0,
                suggestedMax: 100
            }
        }
    };

    // Apple's stage names with the guide's Korean gloss (evaluationRules.tagLabel).
    const tagText = tag => [].concat(tagLabel(tag)).join(' · ');
    const tagOptions = radarLabels.map(tag => ({ key: tag, text: tagText(tag), value: tag }));

    // Set initial selected tag if none selected and options exist
    if (!selectedTag && tagOptions.length > 0) {
        setSelectedTag("discover");
    }

    // Calculate data for selected tag analysis
    let tagHistoryData = null;
    let tagCriteriaStats = [];

    if (selectedTag) {
        const history = [];
        const criteriaMap = {}; // text -> { met, total }

        sortedSessions.forEach(session => {
            if (session.result && session.result.tag_results && session.result.tag_results[selectedTag]) {
                const criteriaList = session.result.tag_results[selectedTag];
                
                criteriaList.forEach(c => {
                    if (!criteriaMap[c.criterion_text]) {
                        criteriaMap[c.criterion_text] = { met: 0, total: 0 };
                    }
                    criteriaMap[c.criterion_text].total += 1;
                    if (c.is_criterion_met) {
                        criteriaMap[c.criterion_text].met += 1;
                    }
                });

                const metCount = criteriaList.filter(c => c.is_criterion_met).length;
                const totalCount = criteriaList.length;
                const percentage = totalCount > 0 ? (metCount / totalCount) * 100 : 0;

                history.push({
                    date: session.created_at,
                    percentage: percentage
                });
            }
        });

        tagHistoryData = {
            labels: history.map(h => moment(h.date).format('MM/DD')),
            datasets: [
                {
                    label: `${tagText(selectedTag)} 기준 충족 %`,
                    data: history.map(h => h.percentage),
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.5)',
                    tension: 0.3
                },
                ...(stats && stats.tag_averages && stats.tag_averages[selectedTag] ? [{
                    label: '회사 평균',
                    data: history.map(() => stats.tag_averages[selectedTag]),
                    borderColor: 'rgba(255, 159, 64, 1)',
                    backgroundColor: 'rgba(255, 159, 64, 0.5)',
                    borderDash: [5, 5],
                    pointRadius: 0,
                    tension: 0.3
                }] : [])
            ]
        };

        tagCriteriaStats = Object.entries(criteriaMap).map(([text, stats]) => ({
            text,
            percentage: (stats.met / stats.total) * 100,
            total: stats.total
        })).sort((a, b) => b.percentage - a.percentage);
    }

    const chartData = {
        labels: sortedSessions.map(s => moment(s.created_at).format('MM/DD')),
        datasets: [
            {
                label: '총점',
                data: sortedSessions.map(s => s.total_score),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                tension: 0.3

            },
            ...(stats ? [{
                label: '회사 평균',
                data: sortedSessions.map(() => stats.global_average),
                borderColor: 'rgba(255, 159, 64, 1)',
                backgroundColor: 'rgba(255, 159, 64, 0.5)',
                borderDash: [5, 5],
                pointRadius: 0,
                tension: 0.3
            }] : [])
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
            x: {
                ticks: {
                    maxTicksLimit: 8
                }
            }
        }
    };

    const mainChartOptions = {
        ...options,
        maintainAspectRatio: false,
        plugins: {
            ...options.plugins,
            title: {
                display: true,
                text: '평가 내역',
            },
        }
    };

    // Comparison Data
    let comparisonData = null;
    let userAverage = 0;
    let trend = null; // 'up', 'down', 'stable'
    
    let lowestScore = null;
    let latestScore = null;

    if (sortedSessions.length > 0) {
        const scores = sortedSessions.map(s => s.total_score);
        lowestScore = Math.min(...scores);
        latestScore = scores[scores.length - 1];

        // Calculate user average total score.
        const totalSum = sortedSessions.reduce((acc, s) => acc + s.total_score, 0);
        userAverage = totalSum / sortedSessions.length;

        // Calculate Trend (Last 5 vs Previous 5)
        if (sortedSessions.length >= 2) {
            const recentSessions = sortedSessions.slice(-5);
            const recentAvg = recentSessions.reduce((acc, s) => acc + s.total_score, 0) / recentSessions.length;
            
            let previousAvg = userAverage; // Default to overall average if not enough history
            if (sortedSessions.length >= 10) {
                const previousSessions = sortedSessions.slice(-10, -5);
                previousAvg = previousSessions.reduce((acc, s) => acc + s.total_score, 0) / previousSessions.length;
            } else if (sortedSessions.length > 5) {
                 const previousSessions = sortedSessions.slice(0, sortedSessions.length - 5);
                 previousAvg = previousSessions.reduce((acc, s) => acc + s.total_score, 0) / previousSessions.length;
            }

            const diff = recentAvg - previousAvg;
            if (diff > 1) trend = 'up';
            else if (diff < -1) trend = 'down';
            else trend = 'stable';
        }
        
        if (stats) comparisonData = {
            labels: ['나의 평균', '회사 평균'],
            datasets: [
                {
                    label: '평균 점수',
                    data: [userAverage, stats.global_average], // Assuming stats.global_average is comparable
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.5)',
                        'rgba(255, 159, 64, 0.5)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 1)',
                        'rgba(255, 159, 64, 1)',
                    ],
                    borderWidth: 1,
                },
            ],
        };
    }

    return (
        <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            {/* Header Section */}
            <div style={{ 
                padding: '16px 0', 
                backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)', 
                marginBottom: '20px',
                position: 'relative',
                zIndex: 100
            }}>
                <Container>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div className="header-icon-desktop-only" style={{ 
                                width: '40px', 
                                flexShrink: 0, // stays a circle when the text beside it wraps
                                height: '40px', 
                                borderRadius: '50%', 
                                backgroundColor: '#F2F2F7', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Icon name="dashboard" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
                            </div>
                            <div>
                                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>사용자 대시보드</h2>
                                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>{targetUser?.full_name || targetUser?.username }</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {isMobile ? (
                                <Dropdown
                                    trigger={
                                        <Button icon basic circular>
                                            <Icon name="ellipsis horizontal" />
                                        </Button>
                                    }
                                    pointing="top right"
                                    icon={null}
                                >
                                    <Dropdown.Menu style={{zIndex: 1000}}>
                                        <Dropdown.Item
                                            as={Link}
                                            to={`/chatsessionslist?trainee=${targetUserId}&page_size=${PAGE_SIZE}`}
                                        >
                                            <Icon name='comments' />
                                            <span className="text">대화 목록</span>
                                        </Dropdown.Item>

                                    </Dropdown.Menu>
                                </Dropdown>
                            ) : (
                                <>
                                    <Button 
                                        as={Link} 
                                        to={`/chatsessionslist?trainee=${targetUserId}&page_size=${PAGE_SIZE}`}
                                        size='small'
                                        basic
                                        icon
                                        labelPosition='left'
                                    >
                                        <Icon name='comments' />
                                        대화 목록
                                    </Button>

                                </>
                            )}
                        </div>
                    </div>
                </Container>
            </div>

            <Container>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* SECTION 1: Sales Skills (판매 스킬)                     */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div style={{
                marginBottom: '3em',
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    marginBottom: '1.5em',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #007AFF'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #007AFF, #0051D5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon name='comments' style={{ margin: 0, color: '#fff', fontSize: '16px' }} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#1d1d1f', letterSpacing: '-0.01em' }}>판매 스킬</h2>
                        <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>롤플레이 기반 성과 분석</p>
                    </div>
                </div>

            {/* Top Stats Row. Phones: two to a row — stacked, each took a full screen width. */}
            {sortedSessions.length > 0 && (
                <Grid columns={isMobile ? 2 : (isAdmin && stats ? 5 : 2)} stackable={!isMobile} style={{ marginBottom: '2em' }}>
                        {isAdmin && stats && (
<Grid.Column >
                            {(() => {
                                const diff = userAverage - stats.global_average;
                                let bgColor, title, msg;

                                if (diff >= 2) {
                                    bgColor = '#f2fcf5'; // Very light green
                                    title = '훌륭합니다!';
                                    msg = '전체 평균보다 높은 성과를 유지하고 있습니다.';
                                } else if (diff <= -2) {
                                    bgColor = '#fff5f5'; // Very light red
                                    title = '분발하세요!';
                                    msg = '전체 평균보다 다소 낮습니다. 개선이 필요합니다.';
                                } else {
                                    bgColor = '#fffaf0'; // Very light orange
                                    title = '평균적인 성과입니다.';
                                    msg = '전체 평균과 비슷한 수준입니다.';
                                }

                                return (
                                    <div style={{ 
                                        borderRadius: '16px', 
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                        border: 'none', 
                                        backgroundColor: bgColor, 
                                        padding: '1.5em', 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#1d1d1f', marginBottom: '0.5em' }}>
                                            {title}
                                        </div>
                                        <div style={{ color: '#424245', lineHeight: '1.5' }}>
                                            {msg}
                                        </div>
                                    </div>
                                );
                            })()}
                        </Grid.Column>
)}
                        <Grid.Column>
                            <div style={{ 
                                borderRadius: '16px', 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                border: 'none', 
                                backgroundColor: '#ffffff', 
                                padding: '1.5em', 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>나의 평균</div>
                                <div style={{ fontSize: isMobile ? '1.8em' : '2.5em', fontWeight: '600', color: '#1d1d1f' }}>
                                    {userAverage.toFixed(1)}
                                    {trend && (
                                        <Icon 
                                            name={trend === 'up' ? 'chart line' : (trend === 'down' ? 'chart line' : 'minus')} 
                                            color={trend === 'up' ? 'green' : (trend === 'down' ? 'red' : 'grey')}
                                            style={{ fontSize: '0.4em', marginLeft: '0.3em', transform: trend === 'down' ? 'scaleY(-1)' : 'none', verticalAlign: 'middle' }}
                                            title={trend === 'up' ? '상승세' : (trend === 'down' ? '하락세' : '변동 없음')}
                                        />
                                    )}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{ 
                                borderRadius: '16px', 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                border: 'none', 
                                backgroundColor: '#ffffff', 
                                padding: '1.5em', 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>최저 점수 대비</div>
                                {sortedSessions.length < 2 ? (
                                    <div style={{ fontSize: '1em', color: '#86868b' }}>평가가 2회 이상 쌓이면 표시됩니다</div>
                                ) : (
                                    <>
                                        <div style={{ fontSize: isMobile ? '1.8em' : '2.5em', fontWeight: '600', color: latestScore > lowestScore ? '#21ba45' : '#1d1d1f' }}>
                                            {latestScore > lowestScore && (
                                                <Icon name='arrow up' style={{ fontSize: '0.6em', marginRight: '0.1em' }} />
                                            )}
                                            {(latestScore - lowestScore).toFixed(1)}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: '#86868b', marginTop: '0.3em' }}>
                                            최저 {lowestScore.toFixed(1)}점 → 최근 {latestScore.toFixed(1)}점
                                        </div>
                                    </>
                                )}
                            </div>
                        </Grid.Column>
                        {isAdmin && stats && (
<Grid.Column>
                            <div style={{ 
                                borderRadius: '16px', 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                border: 'none', 
                                backgroundColor: '#ffffff', 
                                padding: '1.5em', 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>평균 대비</div>
                                <div style={{ fontSize: isMobile ? '1.8em' : '2.5em', fontWeight: '600', color: userAverage >= stats.global_average ? '#21ba45' : '#db2828' }}>
                                    <Icon name={userAverage >= stats.global_average ? 'arrow up' : 'arrow down'} style={{ fontSize: '0.6em', marginRight: '0.1em' }} />
                                    {Math.abs(userAverage - stats.global_average).toFixed(1)}
                                </div>
                            </div>
                        </Grid.Column>
)}
                        {isAdmin && stats && (
<Grid.Column>
                            <div style={{ 
                                borderRadius: '16px', 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                border: 'none', 
                                backgroundColor: '#ffffff', 
                                padding: '1.5em', 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>회사 평균</div>
                                <div style={{ fontSize: isMobile ? '1.8em' : '2.5em', fontWeight: '600', color: '#86868b' }}>
                                    {stats.global_average.toFixed(1)}
                                </div>
                            </div>
                        </Grid.Column>
)}

                    </Grid>
            )}

            {/* Coaching Corner */}
            {coachingTips && coachingTips.length > 0 && (
                <>
                <div style={{ marginTop: '3em', marginBottom: '1.5em' }}>
                    <Header as='h3' style={{ marginBottom: '1.5em', marginLeft: '0.5em' }}>
                        <Icon name='lightbulb outline' />
                        <Header.Content>
                            AI 코칭 인사이트
                            <Header.Subheader>
                                최근 세션 분석을 통한 맞춤형 제안
                            </Header.Subheader>
                        </Header.Content>
                    </Header>
                </div>
                    
                    <Grid columns={3} stackable style={{ marginBottom: '2em' }}>
                        {coachingTips.map((tip, index) => (
                            <Grid.Column key={index}>
                                <Card fluid
                                    // Opens the evaluation the tip's text comes from (the latest one it failed in).
                                    onClick={tip.chat_session_id ? () => navigate(`/chatsessions/${tip.chat_session_id}`) : undefined}
                                    style={{ 
                                    height: '100%', 
                                    borderRadius: '16px', 
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                    border: 'none',
                                    backgroundColor: '#ffffff',
                                    cursor: tip.chat_session_id ? 'pointer' : 'default'
                                }}>
                                    <Card.Content style={{ padding: '1.5em' }}>
                                        <Label size='mini' basic style={{ marginBottom: '1em', borderRadius: '20px', color: '#86868b', borderColor: '#d2d2d7' }}>
                                            {tip.sessions_considered
                                                ? `최근 ${tip.sessions_considered}회 중 ${tip.failure_count}회 미충족`
                                                : `최근 ${tip.failure_count}회 미충족`}
                                        </Label>
                                        <Card.Header style={{ fontSize: '1.1em', fontWeight: '600', marginBottom: '0.8em', color: '#1d1d1f' }}>
                                            {tip.criterion}
                                        </Card.Header>
                                        <Card.Description style={{ fontSize: '1em', lineHeight: '1.6', color: '#424245' }}>
                                            {tip.tip}
                                        </Card.Description>
                                    </Card.Content>
                                </Card>
                            </Grid.Column>
                        ))}
                    </Grid>
                </>
            )}

            {/* Charts Row — part of Sales Skills */}
            <div style={{ marginTop: '2em', marginBottom: '1em' }}>
                <Header as='h4' style={{ marginLeft: '0.5em', color: '#424245' }}>
                    <Icon name='chart line' />
                    <Header.Content>
                        성과 분석
                        <Header.Subheader>
                            평가 내역 및 영역별 상세 분석
                        </Header.Subheader>
                    </Header.Content>
                </Header>
            </div>
            <Grid stackable columns={isAdmin ? 3 : 2}>
                <Grid.Column>
                    <div style={{ 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                        border: 'none', 
                        backgroundColor: '#ffffff', 
                        padding: '1.5em', 
                        height: '100%' 
                    }}>
                        <Header as='h3' style={{ color: '#1d1d1f' }}>평가 내역</Header>
                        {sortedSessions.length > 0 ? (
                            <div style={{ height: '300px' }}>
                                <Line options={mainChartOptions} data={chartData} />
                            </div>
                        ) : (
                            <Message info>평가 세션이 없습니다.</Message>
                        )}
                    </div>
                </Grid.Column>
                <Grid.Column>
                    <div style={{ 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                        border: 'none', 
                        backgroundColor: '#ffffff', 
                        padding: '1.5em', 
                        height: '100%' 
                    }}>
                        <Header as='h3' style={{ color: '#1d1d1f' }}>영역별 성과</Header>
                        {radarLabels.length > 0 ? (
                            <div style={{ height: '300px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <Radar data={radarData} options={radarOptions} />
                            </div>
                        ) : (
                            <Message info>
                                태그 데이터가 없습니다. 평가 세션이 처리되었는지 확인하세요.
                            </Message>
                        )}
                    </div>
                </Grid.Column>
                {isAdmin && (
<Grid.Column>
                    <div style={{ 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                        border: 'none', 
                        backgroundColor: '#ffffff', 
                        padding: '1.5em', 
                        height: '100%' 
                    }}>
                        <Header as='h3' style={{ color: '#1d1d1f' }}>동료 비교</Header>
                        {comparisonData && (
                            <div style={{ height: '300px' }}>
                                <Bar 
                                    data={comparisonData} 
                                    options={{
                                        ...options, 
                                        maintainAspectRatio: false,
                                        indexAxis: 'y',
                                        plugins: { legend: { display: false } }
                                    }} 
                                />
                            </div>
                        )}
                    </div>
                </Grid.Column>
)}
            </Grid>

            {/* Detailed Analysis and Recent Sessions Row */}
            <Grid stackable>
                <Grid.Column width={16}>
                    <div style={{ 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                        border: 'none', 
                        backgroundColor: '#ffffff', 
                        padding: '1.5em', 
                        height: '100%' 
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                            <Header as='h3' style={{ color: '#1d1d1f', margin: 0 }}>상세 영역 분석</Header>
                            <Dropdown
                                placeholder='영역 선택'
                                selection
                                options={tagOptions}
                                value={selectedTag}
                                onChange={(e, { value }) => setSelectedTag(value)}
                                style={{ 
                                    minWidth: '200px', 
                                    backgroundColor: '#f0f8ff', 
                                    border: '1px solid rgba(75, 192, 192, 1)', 
                                    borderRadius: '8px' 
                                }}
                            />
                        </div>
                        
                        {selectedTag && tagHistoryData && (
                            <Grid columns={3} stackable style={{ marginTop: '1em' }}>
                                <Grid.Column>
                                    <Header as='h4' style={{ color: '#424245' }}>시간 경과에 따른 진행 상황: {tagText(selectedTag)}</Header>
                                    <div style={{ height: '250px' }}>
                                        <Line data={tagHistoryData} options={{...options, maintainAspectRatio: false}} />
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    <Header as='h4' style={{ color: '#424245' }}>주요 강점</Header>
                                    <List>
                                        {tagCriteriaStats.filter(c => c.percentage >= 80).slice(0, isMobile ? 3 : 5).map((c, i) => (
                                            <List.Item key={i}>
                                                <List.Content>
                                                    <List.Header style={{ color: '#1d1d1f' }}>{c.text}</List.Header>
                                                    <Progress style={{marginTop:"1em"}} percent={c.percentage.toFixed(0)} size='tiny' color='green' active>
                                                        {c.percentage.toFixed(0)}% ({c.total}회)
                                                    </Progress>
                                                </List.Content>
                                            </List.Item>
                                        ))}
                                        {tagCriteriaStats.filter(c => c.percentage >= 80).length === 0 && <Message size='mini'>아직 식별된 강점이 없습니다.</Message>}
                                    </List>
                                </Grid.Column>
                                <Grid.Column>
                                    <Header as='h4' style={{ color: '#424245' }}>개선 필요 영역</Header>
                                    <List>
                                        {tagCriteriaStats.filter(c => c.percentage < 80).reverse().slice(0, isMobile ? 3 : 5).map((c, i) => (
                                            <List.Item key={i}>
                                                <List.Content>
                                                    <List.Header style={{ color: '#1d1d1f' }}>{c.text}</List.Header>
                                                    <Progress style={{marginTop:"1em"}}  percent={c.percentage.toFixed(0)} size='tiny' color='red' active>
                                                        {c.percentage.toFixed(0)}% ({c.total}회)
                                                    </Progress>
                                                </List.Content>
                                            </List.Item>
                                        ))}
                                            {tagCriteriaStats.filter(c => c.percentage < 80).length === 0 && <Message size='mini'>식별된 취약 영역이 없습니다.</Message>}
                                    </List>
                                </Grid.Column>
                            </Grid>
                        )}
                    </div>
                </Grid.Column>
            </Grid>

            </div>{/* End Sales Skills Section */}

            {/* ═══════════════════════════════════════════════════════ */}
            {/* SECTION 1.5: Dynamic Personas (맞춤 훈련 페르소나)         */}
            {/* ═══════════════════════════════════════════════════════ */}
            {dynamicPersonas?.results?.length > 0 && (
            <div style={{
                marginBottom: '3em',
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    marginBottom: '1.5em',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #FF9500'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #FF9500, #FF6B00)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon name='bullseye' style={{ margin: 0, color: '#fff', fontSize: '16px' }} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#1d1d1f', letterSpacing: '-0.01em' }}>맞춤 훈련 페르소나</h2>
                        <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>취약 영역 개선을 위한 맞춤형 훈련 고객</p>
                    </div>
                </div>

                <Grid columns={3} stackable doubling style={{ marginBottom: '1em' }}>
                    {dynamicPersonas.results.map((persona) => (
                        <Grid.Column key={persona.id}>
                            <Card 
                                fluid 
                                style={{ 
                                    borderRadius: '16px', 
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                                    border: 'none',
                                    backgroundColor: persona.deactivated_at ? '#f5f5f5' : '#ffffff',
                                    opacity: persona.deactivated_at ? 0.7 : 1,
                                }}
                            >
                                <Card.Content style={{ padding: '1.5em' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.8em' }}>
                                        <Card.Header style={{ fontSize: '1.1em', fontWeight: '600', color: persona.deactivated_at ? '#999' : '#1d1d1f' }}>
                                            {persona.name}
                                        </Card.Header>
                                        {persona.deactivated_at && (
                                            <Label size='mini' basic style={{ borderRadius: '20px', color: '#999', borderColor: '#d2d2d7' }}>
                                                비활성
                                            </Label>
                                        )}
                                    </div>
                                    <Card.Meta style={{ marginBottom: '0.8em' }}>
                                        <Label size='small' color='orange' style={{ borderRadius: '20px' }}>
                                            <Icon name='target' />
                                            {(() => {
                                                const aoi = persona.areas_of_improvement;
                                                if (!aoi) return '훈련';
                                                const products = aoi.product_knowledge || [];
                                                const productNames = products.map(p => p.name).filter(Boolean);
                                                return productNames.length ? productNames.join(', ') : '훈련';
                                            })()}
                                        </Label>
                                    </Card.Meta>
                                    <Card.Description style={{ fontSize: '0.9em', lineHeight: '1.5', color: persona.deactivated_at ? '#999' : '#424245' }}>
                                        {(() => {
                                            const aoi = persona.areas_of_improvement;
                                            if (!aoi) return persona.purpose_of_visit || '';
                                            const comm = aoi.communication || [];
                                            const products = aoi.product_knowledge || [];
                                            if (comm.length === 0 && products.length === 0) return persona.purpose_of_visit || '';
                                            const hasComm = comm.length > 0;
                                            const hasProducts = products.length > 0;
                                            return (
                                                <div>
                                                    {hasComm && (
                                                        <div style={{ marginBottom: hasProducts ? '4px' : 0 }}>
                                                            <div style={{ color: '#86868b', marginBottom: '2px' }}>응대 스킬</div>
                                                            <ul style={{ margin: 0 }}>
                                                                {comm.map((c, i) => c.text && (
                                                                    <li key={i}>{c.text}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {hasProducts && (
                                                        <div>
                                                            <div style={{ color: '#86868b', marginBottom: '2px' }}>제품</div>
                                                            <ul style={{ margin: 0 }}>
                                                                {products.map((p, i) => p.name && (
                                                                    <li key={i}>{p.name}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </Card.Description>
                                </Card.Content>
                                {!persona.deactivated_at && (
                                    <Card.Content extra style={{ borderTop: '1px solid #f0f0f0', padding: '1em 1.5em' }}>
                                        <Button
                                            size='small'
                                            basic
                                            color='orange'
                                            fluid
                                            style={{ borderRadius: '10px' }}
                                            loading={startingPersonaId === persona.id}
                                            onClick={() => handleStartTraining(persona.id)}
                                        >
                                            <Icon name='play' />
                                            훈련 시작
                                        </Button>
                                    </Card.Content>
                                )}
                            </Card>
                        </Grid.Column>
                    ))}
                </Grid>
            </div>
            )}

            {/* ═══════════════════════════════════════════════════════ */}
            {/* SECTION 2: Product Knowledge (제품 지식)                 */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div style={{
                marginBottom: '3em',
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    marginBottom: '1.5em',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #34C759'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #34C759, #248A3D)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon name='book' style={{ margin: 0, color: '#fff', fontSize: '16px' }} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#1d1d1f', letterSpacing: '-0.01em' }}>제품 지식</h2>
                        <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>퀴즈 학습 현황 및 제품별 정답률</p>
                    </div>
                </div>

            {(isQuizStatsLoading || isPKEStatsLoading) ? (
                <Segment style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '3em' }}>
                    <Loader active inline='centered' />
                </Segment>
            ) : (quizStats && quizStats.total_attempts > 0) || (pkeStats && pkeStats.total_evaluations > 0) ? (
                <>
                    {/* Summary Cards */}
                    {/* Phones: two to a row, like the 판매 스킬 stats above. */}
                    <Grid columns={isMobile ? 2 : 6} stackable={!isMobile} doubling={!isMobile} style={{ marginBottom: '2em' }}>
                        {pkeStats && pkeStats.total_evaluations > 0 && (
                            <>
                                <Grid.Column>
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>PK 평가</div>
                                        <div style={{ fontSize: '2.2em', fontWeight: '600', color: '#1d1d1f' }}>
                                            {pkeStats.total_evaluations}
                                        </div>
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>PK 평균 점수</div>
                                        <div style={{
                                            fontSize: '2.2em',
                                            fontWeight: '600',
                                            color: pkeStats.avg_score >= 80 ? '#34C759' : pkeStats.avg_score >= 50 ? '#FF9500' : '#FF3B30'
                                        }}>
                                            {pkeStats.avg_score}
                                        </div>
                                    </div>
                                </Grid.Column>
                            </>
                        )}
                        {quizStats && quizStats.total_attempts > 0 && (
                            <>
                                <Grid.Column>
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>퀴즈 정답률</div>
                                        <div style={{
                                            fontSize: '2.2em',
                                            fontWeight: '600',
                                            color: quizStats.accuracy >= 80 ? '#34C759' : quizStats.accuracy >= 50 ? '#FF9500' : '#FF3B30'
                                        }}>
                                            {quizStats.accuracy}%
                                        </div>
                                        {/* accuracy = latest answer per question (what they know now); first try =
                                            what they knew before practising. Repeats no longer inflate either (#249). */}
                                        {quizStats.first_try_accuracy !== undefined && (
                                            <div style={{ fontSize: '0.8em', color: '#86868b', marginTop: '0.3em' }}>
                                                첫 시도 정답률 {quizStats.first_try_accuracy}%
                                            </div>
                                        )}
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    {/* Coverage of what they can be asked + raw effort. The attempt count alone said nothing
                                        (793 could be 793 questions or the same 5 on a loop); with coverage and the
                                        per-question average it reads as how much they've seen and how hard they practised. */}
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>풀이 기록</div>
                                        <div style={{ fontSize: '2.2em', fontWeight: '600', color: '#1d1d1f' }}>
                                            {quizStats.questions_covered ?? quizStats.questions_answered}
                                            {quizStats.questions_available > 0 && (
                                                <span style={{ fontSize: '0.45em', color: '#86868b', fontWeight: 500 }}> / {quizStats.questions_available} 문제</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.8em', color: '#86868b', marginTop: '0.3em', textAlign: 'center', lineHeight: 1.5 }}>
                                            <div style={{ whiteSpace: 'nowrap' }}>총 {quizStats.total_attempts}회 풀이</div>
                                            {quizStats.questions_answered > 0 && (
                                                <div style={{ whiteSpace: 'nowrap' }}>문제당 평균 {Math.round(quizStats.total_attempts / quizStats.questions_answered)}회</div>
                                            )}
                                        </div>
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>연속 학습</div>
                                        <div style={{ fontSize: '2.2em', fontWeight: '600', color: '#1d1d1f' }}>
                                            {quizStats.current_streak > 0 ? (
                                                <><span role="img" aria-label="streak">🔥</span> {quizStats.current_streak}일</>
                                            ) : (
                                                '—'
                                            )}
                                        </div>
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    <div style={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        border: 'none',
                                        backgroundColor: quizStats.due_for_review > 0 ? '#FFF8E1' : '#ffffff',
                                        padding: '1.5em',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9em', color: '#86868b', marginBottom: '0.5em' }}>복습 필요</div>
                                        <div style={{ fontSize: '2.2em', fontWeight: '600', color: quizStats.due_for_review > 0 ? '#FF9500' : '#1d1d1f' }}>
                                            {quizStats.due_for_review}
                                        </div>
                                    </div>
                                </Grid.Column>
                            </>
                        )}
                    </Grid>

                    {/* Charts Row — matched height */}
                    {(() => {
                        // Category color palette
                        const CATEGORY_COLORS = {
                            'iphone': { bg: 'rgba(0, 122, 255, 0.85)', bgLight: 'rgba(0, 122, 255, 0.3)', border: '#007AFF' },
                            'mac': { bg: 'rgba(88, 86, 214, 0.85)', bgLight: 'rgba(88, 86, 214, 0.3)', border: '#5856D6' },
                            'ipad': { bg: 'rgba(52, 199, 89, 0.85)', bgLight: 'rgba(52, 199, 89, 0.3)', border: '#34C759' },
                            'watch': { bg: 'rgba(255, 149, 0, 0.85)', bgLight: 'rgba(255, 149, 0, 0.3)', border: '#FF9500' },
                            'airpods': { bg: 'rgba(255, 59, 48, 0.85)', bgLight: 'rgba(255, 59, 48, 0.3)', border: '#FF3B30' },
                            'apple tv': { bg: 'rgba(175, 82, 222, 0.85)', bgLight: 'rgba(175, 82, 222, 0.3)', border: '#AF52DE' },
                            'services': { bg: 'rgba(0, 199, 190, 0.85)', bgLight: 'rgba(0, 199, 190, 0.3)', border: '#00C7BE' },
                            'accessories': { bg: 'rgba(255, 45, 85, 0.85)', bgLight: 'rgba(255, 45, 85, 0.3)', border: '#FF2D55' },
                            'vision': { bg: 'rgba(100, 210, 255, 0.85)', bgLight: 'rgba(100, 210, 255, 0.3)', border: '#64D2FF' },
                            'homepod': { bg: 'rgba(255, 214, 10, 0.85)', bgLight: 'rgba(255, 214, 10, 0.3)', border: '#FFD60A' },
                        };
                        const DEFAULT_COLOR = { bg: 'rgba(142, 142, 147, 0.85)', bgLight: 'rgba(142, 142, 147, 0.3)', border: '#8E8E93' };

                        const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_COLOR;

                        // Merge quiz + PKE by-product data, sorted by quiz attempts, limited to top 10
                        const mergedProducts = {};

                        // Add quiz data
                        if (quizStats?.by_product) {
                            Object.entries(quizStats.by_product).forEach(([pid, p]) => {
                                mergedProducts[pid] = {
                                    product_name: p.product_name,
                                    category: p.category || '',
                                    quiz_total: p.total,
                                    quiz_correct: p.correct,
                                    quiz_accuracy: p.total > 0 ? Math.round((p.correct / p.total) * 100) : null,
                                    pke_avg_score: null,
                                    pke_count: 0,
                                };
                            });
                        }

                        // Overlay PKE data
                        if (pkeStats?.by_product) {
                            Object.entries(pkeStats.by_product).forEach(([pid, p]) => {
                                if (mergedProducts[pid]) {
                                    mergedProducts[pid].pke_avg_score = p.avg_score;
                                    mergedProducts[pid].pke_count = p.eval_count;
                                    if (!mergedProducts[pid].category) mergedProducts[pid].category = p.category || '';
                                } else {
                                    mergedProducts[pid] = {
                                        product_name: p.product_name,
                                        category: p.category || '',
                                        quiz_total: 0,
                                        quiz_correct: 0,
                                        quiz_accuracy: null,
                                        pke_avg_score: p.avg_score,
                                        pke_count: p.eval_count,
                                    };
                                }
                            });
                        }

                        // Sort by total activity descending (quiz attempts + PKE count), top 10
                        const sortedProducts = Object.values(mergedProducts)
                            .sort((a, b) => (b.quiz_total + b.pke_count) - (a.quiz_total + a.pke_count))
                            .slice(0, 10);

                        // Group by category for labels & ordering
                        const categories = [...new Set(sortedProducts.map(p => p.category || '기타'))];
                        const sortedByCategory = [];
                        categories.forEach(cat => {
                            sortedProducts.filter(p => (p.category || '기타') === cat).forEach(p => sortedByCategory.push(p));
                        });

                        const hasQuizData = sortedByCategory.some(p => p.quiz_accuracy !== null);
                        const hasPKEData = sortedByCategory.some(p => p.pke_avg_score !== null);

                        const CHART_HEIGHT = '360px';

                        return (
                            <Grid stackable columns={2} style={{ marginBottom: '2em' }}>
                                {/* Per-product chart */}
                                {sortedByCategory.length > 0 && (
                                    <Grid.Column>
                                        <div style={{
                                            borderRadius: '16px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                            border: 'none',
                                            backgroundColor: '#ffffff',
                                            padding: '1.5em',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
                                                <Header as='h4' style={{ color: '#1d1d1f', margin: 0 }}>제품별 정답률</Header>
                                                {/* Category legend */}
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {categories.map(cat => (
                                                        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: getCategoryColor(cat).border }} />
                                                            <span style={{ fontSize: '0.7em', color: '#86868b' }}>{cat}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1, minHeight: CHART_HEIGHT }}>
                                                <Bar
                                                    data={{
                                                        labels: sortedByCategory.map(p => p.product_name),
                                                        datasets: [
                                                            ...(hasQuizData ? [{
                                                                label: '퀴즈 정답률 (%)',
                                                                data: sortedByCategory.map(p => p.quiz_accuracy),
                                                                backgroundColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').bg),
                                                                borderColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').border),
                                                                borderWidth: 1,
                                                                borderRadius: 4,
                                                            }] : []),
                                                            ...(hasPKEData ? [{
                                                                label: 'PK 평가 점수',
                                                                data: sortedByCategory.map(p => p.pke_avg_score),
                                                                backgroundColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').bgLight),
                                                                borderColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').border),
                                                                borderWidth: 1,
                                                                borderDash: [4, 4],
                                                                borderRadius: 4,
                                                            }] : []),
                                                        ],
                                                    }}
                                                    options={{
                                                        indexAxis: 'y',
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            legend: { display: hasPKEData && hasQuizData, position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                                                            tooltip: {
                                                                callbacks: {
                                                                    afterLabel: (ctx) => {
                                                                        const p = sortedByCategory[ctx.dataIndex];
                                                                        const lines = [];
                                                                        if (p.quiz_total > 0) lines.push(`퀴즈: ${p.quiz_correct}/${p.quiz_total}`);
                                                                        if (p.pke_count > 0) lines.push(`PK 평가: ${p.pke_count}회`);
                                                                        lines.push(`카테고리: ${p.category || '기타'}`);
                                                                        return lines.join('\n');
                                                                    }
                                                                }
                                                            }
                                                        },
                                                        scales: {
                                                            x: { min: 0, max: 100, grid: { display: false }, ticks: { callback: v => v + '%' } },
                                                            // Long product names were cut off at the left edge on phones.
                                                            y: { grid: { display: false }, ticks: { font: { size: 11 }, callback: function (v) {
                                                                const name = this.getLabelForValue(v);
                                                                const max = isMobile ? 14 : 28;
                                                                return name.length > max ? `${name.slice(0, max - 1)}…` : name;
                                                            } } }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </Grid.Column>
                                )}

                                {/* MC vs OE breakdown doughnut */}
                                {quizStats && quizStats.total_attempts > 0 && (
                                    <Grid.Column>
                                        <div style={{
                                            borderRadius: '16px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                            border: 'none',
                                            backgroundColor: '#ffffff',
                                            padding: '1.5em',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            <Header as='h4' style={{ color: '#1d1d1f', marginBottom: '1em' }}>유형별 분석</Header>
                                            <div style={{ flex: 1, minHeight: CHART_HEIGHT, display: 'flex', alignItems: 'center' }}>
                                                {quizStats.mc_attempts + quizStats.oe_attempts === 0 ? (
                                                    // An empty doughnut read as a broken chart.
                                                    <div style={{ width: '100%', textAlign: 'center', color: '#86868b', fontSize: '14px' }}>
                                                        아직 집계할 퀴즈 기록이 없습니다.
                                                    </div>
                                                ) : (
                                                <Grid columns={2} stackable style={{ width: '100%' }}>
                                                    <Grid.Column style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <div style={{ width: '180px', height: '180px' }}>
                                                            <Doughnut
                                                                data={{
                                                                    labels: ['객관식 정답', '객관식 오답', '서술형 정답', '서술형 오답'],
                                                                    datasets: [{
                                                                        data: [
                                                                            quizStats.mc_correct,
                                                                            quizStats.mc_attempts - quizStats.mc_correct,
                                                                            quizStats.oe_correct,
                                                                            quizStats.oe_attempts - quizStats.oe_correct,
                                                                        ],
                                                                        backgroundColor: [
                                                                            'rgba(0, 122, 255, 0.8)',
                                                                            'rgba(0, 122, 255, 0.2)',
                                                                            'rgba(52, 199, 89, 0.8)',
                                                                            'rgba(52, 199, 89, 0.2)',
                                                                        ],
                                                                        borderWidth: 0,
                                                                    }]
                                                                }}
                                                                options={{
                                                                    responsive: true,
                                                                    maintainAspectRatio: true,
                                                                    cutout: '65%',
                                                                    plugins: {
                                                                        legend: { display: false },
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </Grid.Column>
                                                    <Grid.Column>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', height: '100%' }}>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: 'rgba(0, 122, 255, 0.8)' }} />
                                                                    <span style={{ fontSize: '0.9em', color: '#86868b' }}>객관식 (MC)</span>
                                                                </div>
                                                                <div style={{ fontSize: '1.5em', fontWeight: '600', color: '#1d1d1f' }}>
                                                                    {quizStats.mc_accuracy}%
                                                                    <span style={{ fontSize: '0.5em', color: '#86868b', marginLeft: '6px' }}>
                                                                        {quizStats.mc_correct}/{quizStats.mc_attempts}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: 'rgba(52, 199, 89, 0.8)' }} />
                                                                    <span style={{ fontSize: '0.9em', color: '#86868b' }}>서술형 (OE)</span>
                                                                </div>
                                                                <div style={{ fontSize: '1.5em', fontWeight: '600', color: '#1d1d1f' }}>
                                                                    {quizStats.oe_accuracy}%
                                                                    <span style={{ fontSize: '0.5em', color: '#86868b', marginLeft: '6px' }}>
                                                                        {quizStats.oe_correct}/{quizStats.oe_attempts}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div style={{ borderTop: '1px solid #E5E5EA', paddingTop: '12px' }}>
                                                                {/* Questions by their latest answer, not attempts (#249). */}
                                                                <div style={{ fontSize: '0.85em', color: '#86868b' }}>전체 정답/오답 (문제 기준)</div>
                                                                <div style={{ fontSize: '1.2em', fontWeight: '600', color: '#1d1d1f', marginTop: '4px' }}>
                                                                    {quizStats.correct_attempts}
                                                                    <span style={{ color: '#34C759' }}> 정답</span>
                                                                    {' / '}
                                                                    {(quizStats.questions_answered ?? quizStats.total_attempts) - quizStats.correct_attempts}
                                                                    <span style={{ color: '#FF3B30' }}> 오답</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Grid.Column>
                                                </Grid>
                                                )}
                                            </div>
                                        </div>
                                    </Grid.Column>
                                )}
                            </Grid>
                        );
                    })()}
                </>
            ) : (
                <div style={{
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: 'none',
                    backgroundColor: '#ffffff',
                    padding: '2.5em',
                    textAlign: 'center',
                    marginBottom: '2em'
                }}>
                    <Icon name='book' size='big' style={{ color: '#d2d2d7', marginBottom: '12px' }} />
                    <p style={{ color: '#86868b', fontSize: '15px', margin: '0.5em 0' }}>아직 퀴즈 학습 기록이 없습니다.</p>
                    {(!userIdParam) && (
                        <Button
                            as={Link}
                            to='/quiz/dashboard'
                            size='small'
                            basic
                            color='blue'
                            style={{ marginTop: '1em', borderRadius: '20px' }}
                        >
                            <Icon name='play' /> 퀴즈 시작하기
                        </Button>
                    )}
                </div>
            )}

            </div>{/* End Product Knowledge Section */}

            </Container>
        </div>
    );
};

export default UserDashboardPage;
